///
/// EarthBrowser
///
/// Created by Matt Giger on 9/13/2026
/// Copyright (c) 2026 Geodata Labs
///
/// One-time contextual conversion prompt: when an anonymous user saves
/// their second view, point out that views live only on this device and a
/// free account keeps them everywhere. Fires at the moment of saving (never
/// retroactively on launch), once per install, and leans on the existing
/// local-to-cloud saved-view migration so nothing is lost at signup.
///

import { Button } from '@/components/ui/button'
import { getBetaStatus } from '@/hooks/useBetaStatus'
import { setAuthOpen } from '@/redux/dialogSlice'
import { recordSavedViewPromptShown } from '@/redux/engagementSlice'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { useGetUserProfileQuery } from '@/redux/userApi'
import { track } from '@/utils/analytics'
import { XIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { slotActionClass, slotDismissClass, slotPillClass, slotPriority, useNotificationSlot } from './NotificationSlot'

export function SavedViewPrompt() {
	const dispatch = useAppDispatch()
	const { data: userProfile, isLoading } = useGetUserProfileQuery()
	const { isBeta } = getBetaStatus()
	const promptShown = useAppSelector(state => state.engagement.savedViewPromptShown)
	const viewCount = useAppSelector(state => Object.keys(state.savedViews.views).length)
	const [wants, setWants] = useState(false)
	const visible = useNotificationSlot('saved-view-prompt', wants, slotPriority.savedViewPrompt)

	// Trigger on the save action itself: the count must *increase* past 2
	// during this session. A returning visitor who already has two views
	// never sees it out of context at launch.
	const previousCount = useRef<number | null>(null)
	useEffect(() => {
		const previous = previousCount.current
		previousCount.current = viewCount
		if (previous === null || viewCount <= previous) return

		const anonymous = !isBeta && !isLoading && userProfile === undefined
		if (anonymous && viewCount >= 2 && !promptShown) {
			setWants(true)
			dispatch(recordSavedViewPromptShown())
			track('saved_view_prompt_shown', { view_count: viewCount })
		}
	}, [viewCount, isBeta, isLoading, userProfile, promptShown, dispatch])

	if (!visible) return null

	const handleRegister = () => {
		track('saved_view_prompt_clicked', {})
		setWants(false)
		dispatch(setAuthOpen(true))
	}

	const handleDismiss = () => {
		track('saved_view_prompt_dismissed', {})
		setWants(false)
	}

	return (
		<div className={slotPillClass}>
			<span className="text-xs text-white/90 truncate">
				Your views live on this device — a free account keeps them everywhere
			</span>
			<Button
				size="sm"
				onClick={handleRegister}
				className={`${slotActionClass} bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white transition-all duration-300 hover:scale-105`}
			>
				Create free account
			</Button>
			<button
				onClick={handleDismiss}
				aria-label="Dismiss"
				className={slotDismissClass}
			>
				<XIcon className="h-3.5 w-3.5" />
			</button>
		</div>
	)
}
